import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuthContext } from '@/contexts/AuthContext';

export interface Profile {
  id: string;
  username: string | null;
  avatar_url: string | null;
  api_calls_total: number;
  api_calls_month: number;
  joined_at: string;
  updated_at: string;
  email: string | null;
  role: 'admin' | 'user';
}

export function useProfile() {
  const { user } = useAuthContext();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    setLoading(true);

    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .maybeSingle();

    if (!error && data) {
      setProfile(data as Profile);
    } else if (!data) {
      // Profile doesn't exist yet — create it (handles old accounts before trigger)
      const { data: created } = await supabase
        .from('profiles')
        .insert({ id: user.id, username: user.username })
        .select()
        .maybeSingle();
      if (created) setProfile(created as Profile);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const updateProfile = useCallback(async (updates: Partial<Pick<Profile, 'username' | 'avatar_url'>>) => {
    if (!user) return false;
    const { error } = await supabase
      .from('profiles')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', user.id);
    if (!error) {
      setProfile(prev => prev ? { ...prev, ...updates } : prev);
    }
    return !error;
  }, [user]);

  return { profile, loading, refetch: fetchProfile, updateProfile };
}
